const scriptPromises = new Map<string, Promise<void>>();

/**
 * Loads an external script once per page; repeated calls (SPA remounts) reuse
 * the same promise. The script element is never removed on unmount.
 */
export function loadScriptOnce(
  src: string,
  options?: { type?: "module"; crossOrigin?: "anonymous" },
): Promise<void> {
  const existing = scriptPromises.get(src);
  if (existing) {
    return existing;
  }

  const promise = new Promise<void>((resolve, reject) => {
    if (document.querySelector(`script[src="${src}"]`)) {
      resolve();
      return;
    }
    const script = document.createElement("script");
    script.src = src;
    script.async = true;
    if (options?.type) {
      script.type = options.type;
    }
    if (options?.crossOrigin) {
      script.crossOrigin = options.crossOrigin;
    }
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromises.delete(src);
      reject(new Error(`Failed to load script: ${src}`));
    };
    document.head.append(script);
  });
  scriptPromises.set(src, promise);
  return promise;
}

/**
 * Remove a script and forget its cached load promise, so a later loadScriptOnce
 * re-adds it fresh. Used to reset an SDK whose global state can't be torn down.
 */
export function unloadScript(src: string): void {
  scriptPromises.delete(src);
  for (const script of document.querySelectorAll(`script[src="${src}"]`)) {
    script.remove();
  }
}

/** Polls until the getter returns a value or the timeout elapses. */
export function waitForGlobal<T>(
  get: () => T | undefined,
  timeoutMs = 5000,
): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const start = Date.now();
    const check = () => {
      const value = get();
      if (value !== undefined) {
        resolve(value);
      } else if (Date.now() - start > timeoutMs) {
        reject(new Error("Timed out waiting for SDK global"));
      } else {
        setTimeout(check, 50);
      }
    };
    check();
  });
}
